import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Calendar } from './ui/calendar';
import {
  Popover, PopoverContent, PopoverTrigger
} from './ui/popover';
import { Button } from './ui/button';
import {
  PhoneCall, Clock, Activity, CalendarDays, Users, Radio,
  RefreshCw, Search, ChevronLeft, ChevronRight,
  FileText, Upload, LogIn, LogOut
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;

const isoDate = (d) => {
  const dt = (d instanceof Date) ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtClock = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch (e) {
    return '';
  }
};

const durationSec = (startIso, endIso) => {
  if (!startIso) return 0;
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return Math.max(0, Math.floor((end - start) / 1000));
};

const fmtDuration = (sec) => {
  if (sec <= 0) return '0s';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const StatTile = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className={`p-4 flex items-center gap-3 ${accent || 'bg-blue-50/50'}`} data-testid={testId}>
    <div className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center">
      <Icon size={20} className="text-[#1565c0]" />
    </div>
    <div className="leading-tight">
      <div className="text-2xl font-extrabold text-[#0d47a1]">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  </Card>
);

export const LiveActivityView = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [live, setLive] = useState(null);
  const [tick, setTick] = useState(0); // re-render every 1s so durations move forward
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Poll live state every POLL_MS
  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const r = await axios.get(`${API}/admin/activity/live`, { withCredentials: true });
        if (!cancelled) setLive(r.data);
      } catch (e) {
        // silent — don't toast every 5s if the network is flaky
      }
    };
    fetchLive();
    const id = setInterval(fetchLive, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Re-render every second so the active-call duration ticks
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch logs for the selected date
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLogsLoading(true);
      try {
        const date = isoDate(selectedDate);
        const r = await axios.get(`${API}/admin/activity-logs?date=${date}&limit=500`, { withCredentials: true });
        if (!cancelled) setLogs(r.data || []);
      } catch (e) {
        if (!cancelled) toast.error(t('فشل تحميل السجل', 'Failed to load activity', 'Échec'));
      } finally {
        if (!cancelled) setLogsLoading(false);
      }
    };
    load();
  }, [selectedDate, t]);

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      `${l.action || ''} ${l.details || ''} ${l.user_role || ''}`.toLowerCase().includes(q)
    );
  }, [logs, search]);

  const shiftDay = (delta) => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + delta);
    setSelectedDate(next);
  };

  const isToday = isoDate(selectedDate) === isoDate(new Date());

  return (
    <div className="space-y-5" data-testid="live-activity-view">
      {/* Live stats */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Radio size={16} className="text-green-500 animate-pulse" />
          <h3 className="font-bold text-[#0d47a1]">{t('النشاط المباشر', 'Live Now', 'En direct')}</h3>
          <span className="text-xs text-gray-400 ms-auto" data-testid="live-now-timestamp">
            {live ? t('آخر تحديث', 'Updated', 'MAJ') + ' ' + fmtClock(live.now) : '...'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="live-stat-tiles">
          <StatTile icon={PhoneCall} label={t('مكالمات نشطة', 'Active Calls', 'En cours')} value={live?.stats.active_calls ?? '—'} testId="stat-active-calls" accent="bg-green-50" />
          <StatTile icon={Clock} label={t('في الانتظار', 'Waiting', 'En attente')} value={live?.stats.waiting_calls ?? '—'} testId="stat-waiting" accent="bg-yellow-50" />
          <StatTile icon={Users} label={t('متصلون', 'Online Providers', 'En ligne')} value={live?.stats.online_providers ?? '—'} testId="stat-online" />
          <StatTile icon={Activity} label={t('متاحون', 'Available', 'Disponibles')} value={live?.stats.available_providers ?? '—'} testId="stat-available" />
        </div>
      </div>

      {/* Live calls */}
      <Card className="p-4" data-testid="live-calls-card">
        <h4 className="font-bold text-[#0d47a1] text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
          <PhoneCall size={14} /> {t('المكالمات النشطة', 'Active Calls', 'Appels actifs')}
        </h4>
        {(!live || live.active_calls.length === 0) ? (
          <p className="text-xs text-gray-500">{t('لا توجد مكالمات نشطة', 'No active calls right now', 'Aucun appel actif')}</p>
        ) : (
          <ul className="space-y-2">
            {live.active_calls.map((c) => (
              <li key={c._id} className={`flex items-center justify-between gap-3 p-3 rounded-xl bg-green-50/50 border border-green-100 ${isRTL ? 'flex-row-reverse text-right' : ''}`} data-testid={`live-active-${c._id}`}>
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-bold text-[#0d47a1]">{c.patient_name} <span className="text-gray-400 mx-1">→</span> {c.doctor_name}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{c.call_type} · {fmtClock(c.created_at)}</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-green-700 bg-white px-2 py-1 rounded-full">
                  {/* tick state ensures duration re-renders every second */}
                  {fmtDuration(durationSec(c.created_at) + (tick * 0))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Waiting calls */}
      <Card className="p-4" data-testid="live-waiting-card">
        <h4 className="font-bold text-[#0d47a1] text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
          <Clock size={14} /> {t('قائمة الانتظار', 'Waiting Queue', "File d'attente")}
        </h4>
        {(!live || live.waiting_calls.length === 0) ? (
          <p className="text-xs text-gray-500">{t('لا أحد في الانتظار', 'No one waiting', 'Personne en attente')}</p>
        ) : (
          <ul className="space-y-2">
            {live.waiting_calls.map((c) => (
              <li key={c._id} className={`flex items-center justify-between gap-3 p-3 rounded-xl bg-yellow-50/60 border border-yellow-100 ${isRTL ? 'flex-row-reverse text-right' : ''}`} data-testid={`live-waiting-${c._id}`}>
                <div>
                  <p className="text-sm font-bold text-[#0d47a1]">#{c.position} · {c.patient_name}</p>
                  <p className="text-xs text-gray-500">{t('في انتظار', 'Waiting for', 'Attend')} {c.doctor_name} · {fmtClock(c.created_at)}</p>
                </div>
                <span className="text-xs font-bold text-yellow-700 bg-white px-2 py-1 rounded-full">
                  {fmtDuration(durationSec(c.created_at) + (tick * 0))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Live "Right Now" feed — notes, uploads, joins in the last 5 minutes */}
      <Card className="p-4" data-testid="right-now-card">
        <h4 className="font-bold text-[#0d47a1] text-sm uppercase tracking-wide flex items-center gap-2 mb-3">
          <Activity size={14} className="animate-pulse text-green-600" />
          {t('يحدث الآن', 'Right Now', "En ce moment")}
          <span className="text-xs text-gray-400 font-normal normal-case">
            ({t('آخر 5 دقائق', 'last 5 min', '5 dern. min')})
          </span>
        </h4>
        {(() => {
          const events = [];
          (live?.recent_notes || []).forEach((n) => events.push({
            kind: 'note', id: `n-${n._id}`, time: n.created_at,
            icon: FileText, color: 'text-purple-600 bg-purple-50',
            text: t(
              `كتب الطبيب ${n.doctor_name || ''} ملاحظة للمريض ${n.patient_name || ''}`,
              `Dr ${n.doctor_name || '?'} wrote a note for ${n.patient_name || '?'}`,
              `Dr ${n.doctor_name || '?'} a écrit une note pour ${n.patient_name || '?'}`
            )
          }));
          (live?.recent_files || []).forEach((f) => events.push({
            kind: 'upload', id: `f-${f._id}`, time: f.uploaded_at || f.created_at,
            icon: Upload, color: 'text-blue-600 bg-blue-50',
            text: t(
              `${f.uploader_name || 'مستخدم'} رفع ملف ${f.original_filename || ''}`,
              `${f.uploader_name || 'User'} uploaded ${f.original_filename || ''}`,
              `${f.uploader_name || 'Utilisateur'} a téléversé ${f.original_filename || ''}`
            )
          }));
          (live?.recent_joins || []).forEach((j) => {
            const offline = j.action === 'availability_offline';
            events.push({
              kind: 'join', id: `j-${j._id}`, time: j.created_at,
              icon: offline ? LogOut : LogIn,
              color: offline ? 'text-gray-500 bg-gray-100' : 'text-green-700 bg-green-50',
              text: j.action === 'login'
                ? t(`${j.user_name || 'مستخدم'} سجّل دخوله`, `${j.user_name || 'User'} signed in`, `${j.user_name || 'Utilisateur'} s'est connecté`)
                : j.action === 'register'
                  ? t(`${j.user_name || 'مستخدم'} انضم إلى التطبيق`, `${j.user_name || 'User'} joined the app`, `${j.user_name || 'Utilisateur'} a rejoint`)
                  : j.action === 'availability_online'
                    ? t(`${j.user_name || 'مستخدم'} أصبح متاحاً`, `${j.user_name || 'User'} went online`, `${j.user_name || 'Utilisateur'} en ligne`)
                    : t(`${j.user_name || 'مستخدم'} أصبح غير متاح`, `${j.user_name || 'User'} went offline`, `${j.user_name || 'Utilisateur'} hors ligne`)
            });
          });
          events.sort((a, b) => new Date(b.time) - new Date(a.time));
          const top = events.slice(0, 25);
          if (top.length === 0) {
            return <p className="text-xs text-gray-500">{t('لا يوجد نشاط حالياً', 'No activity right now', 'Pas d\'activité')}</p>;
          }
          return (
            <ul className="space-y-2" data-testid="right-now-feed">
              {top.map((ev) => {
                const Icon = ev.icon;
                return (
                  <li key={ev.id} className={`flex items-center gap-3 p-2 rounded-lg ${isRTL ? 'flex-row-reverse text-right' : ''}`} data-testid={`rn-${ev.kind}-${ev.id}`}>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ev.color}`}>
                      <Icon size={14} />
                    </span>
                    <span className="text-sm text-gray-700 flex-1 truncate">{ev.text}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{fmtClock(ev.time)}</span>
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </Card>

      {/* History calendar + filtered activity log */}
      <Card className="p-4" data-testid="activity-history-card">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h4 className="font-bold text-[#0d47a1] text-sm uppercase tracking-wide flex items-center gap-2">
            <CalendarDays size={14} /> {t('النشاط السابق', 'Previous Activity', 'Activité précédente')}
          </h4>

          <div className="ms-auto flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => shiftDay(-1)} data-testid="activity-prev-day">
              {isRTL ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="activity-date-picker">
                  <CalendarDays size={14} className="me-1" />
                  {isToday ? t('اليوم', 'Today', "Aujourd'hui") : selectedDate.toLocaleDateString()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  disabled={(d) => d > new Date()}
                />
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={() => shiftDay(1)} disabled={isToday} data-testid="activity-next-day">
              {isRTL ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
            </Button>
            {!isToday && (
              <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())} data-testid="activity-today">
                <RefreshCw size={14} className="me-1" />
                {t('اليوم', 'Today', "Aujourd'hui")}
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search size={14} className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('ابحث في السجل...', 'Search this day...', 'Rechercher...')}
            className={`h-9 text-sm ${isRTL ? 'pr-9' : 'pl-9'}`}
            data-testid="activity-search-input"
          />
        </div>

        <div className="text-xs text-gray-500 mb-2">
          {logsLoading ? t('جاري التحميل...', 'Loading...', 'Chargement...') : `${filteredLogs.length} ${t('حدث', 'events', 'événements')}`}
        </div>

        <ul className="space-y-2 max-h-[480px] overflow-auto pr-1">
          {filteredLogs.map((log) => (
            <li key={log._id} className={`p-3 rounded-xl bg-gray-50 border border-gray-100 text-sm ${isRTL ? 'text-right' : ''}`} data-testid={`activity-log-${log._id}`}>
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <span className="font-semibold text-[#0d47a1]">{log.action}</span>
                <span className="text-[10px] uppercase font-bold tracking-wide text-gray-400">{log.user_role}</span>
              </div>
              {log.details && <p className="text-gray-600 text-xs">{log.details}</p>}
              <p className="text-[11px] text-gray-400 mt-1">{fmtClock(log.timestamp)}</p>
            </li>
          ))}
          {!logsLoading && filteredLogs.length === 0 && (
            <li className="p-6 text-center text-gray-400 text-sm">
              {t('لا توجد أحداث لهذا اليوم', 'No events on this day', 'Aucun événement')}
            </li>
          )}
        </ul>
      </Card>
    </div>
  );
};

export default LiveActivityView;
